import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import GavelIcon from '@mui/icons-material/Gavel';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import {
  OFICIO_STATUS_COLOR,
  OFICIO_STATUS_LABEL,
  formatDateBr,
} from './juridicoUtils';

export default function JuridicoOficiosPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada, user, isAdmin, rbacRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [erro, setErro] = useState('');
  const [orgaos, setOrgaos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filtros, setFiltros] = useState({
    q: '',
    status: '',
    orgaoId: '',
    categoriaAssuntoId: '',
    chip: 'pendentes_mim',
  });
  const [excluir, setExcluir] = useState({ open: false, item: null, loading: false });

  const podeExcluirLocal = (it) => {
    if (it?.podeExcluir === true) return true;
    if (it?.podeExcluir === false) return false;
    if (isAdmin || rbacRole === 'SUPER_ADMIN' || rbacRole === 'LOCAL_ADMIN') return true;
    return Number(it?.criadoPor) === Number(user?.id);
  };

  const carregarAux = useCallback(async () => {
    try {
      const [rOrg, rCat] = await Promise.all([
        apiFetch('/api/juridico/orgaos-emissores'),
        apiFetch('/api/juridico/categorias-assunto'),
      ]);
      const dOrg = await rOrg.json().catch(() => []);
      const dCat = await rCat.json().catch(() => []);
      if (rOrg.ok) setOrgaos(Array.isArray(dOrg) ? dOrg : []);
      if (rCat.ok) setCategorias(Array.isArray(dCat) ? dCat : []);
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      Object.entries(filtros).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });
      const res = await apiFetch(`/api/juridico/oficios?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao listar ofícios');
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filtros, unidadeSelecionada?.codigo]);

  useEffect(() => {
    carregarAux();
  }, [carregarAux]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const confirmarExclusao = async () => {
    if (!excluir.item?.id) return;
    setExcluir((p) => ({ ...p, loading: true }));
    try {
      const res = await apiFetch(`/api/juridico/oficios/${excluir.item.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao excluir');
      setExcluir({ open: false, item: null, loading: false });
      carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao excluir');
      setExcluir((p) => ({ ...p, loading: false }));
    }
  };

  const chips = [
    { key: '', label: 'Todos' },
    { key: 'pendentes_mim', label: 'Pendentes para mim' },
    { key: 'atrasados', label: 'Atrasados' },
    { key: 'vencendo_7', label: 'Vencendo em 7 dias' },
    { key: 'meus', label: 'Meus ofícios' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <GavelIcon color="primary" />
            <Typography variant="h5" fontWeight={800}>
              Jurídico — Ofícios
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Unidade: {unidadeSelecionada?.codigo || unidadeSelecionada?.nome || '—'} · {total} registro(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={carregar} sx={{ textTransform: 'none' }}>
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/juridico/oficios/novo')}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Novo Ofício
          </Button>
        </Stack>
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {chips.map((c) => (
            <Chip
              key={c.key || 'all'}
              label={c.label}
              color={filtros.chip === c.key ? 'primary' : 'default'}
              variant={filtros.chip === c.key ? 'filled' : 'outlined'}
              onClick={() => setFiltros((p) => ({ ...p, chip: c.key }))}
            />
          ))}
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Buscar (número / assunto)"
              size="small"
              fullWidth
              value={filtros.q}
              onChange={(e) => setFiltros((p) => ({ ...p, q: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filtros.status}
                onChange={(e) => setFiltros((p) => ({ ...p, status: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(OFICIO_STATUS_LABEL)
                  .filter(([k]) => k !== 'atrasado')
                  .map(([k, lab]) => (
                    <MenuItem key={k} value={k}>
                      {lab}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Órgão</InputLabel>
              <Select
                label="Órgão"
                value={filtros.orgaoId}
                onChange={(e) => setFiltros((p) => ({ ...p, orgaoId: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {orgaos.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={filtros.categoriaAssuntoId}
                onChange={(e) => setFiltros((p) => ({ ...p, categoriaAssuntoId: e.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {categorias.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Assunto</TableCell>
                <TableCell>Órgão</TableCell>
                <TableCell>Recebimento</TableCell>
                <TableCell>Limite</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Responsável</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!items.length ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                      Nenhum ofício encontrado nesta unidade.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it) => {
                  const st = it.statusEfetivo || it.status;
                  return (
                    <TableRow key={it.id} hover>
                      <TableCell>
                        <Typography fontWeight={700} variant="body2">
                          {it.assunto}
                        </Typography>
                        {it.numeroOficio && (
                          <Typography variant="caption" color="text.secondary">
                            Nº {it.numeroOficio}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{it.orgaoNome || '—'}</TableCell>
                      <TableCell>{formatDateBr(it.dataRecebimento)}</TableCell>
                      <TableCell>{formatDateBr(it.dataLimiteResposta)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={OFICIO_STATUS_LABEL[st] || st}
                          color={OFICIO_STATUS_COLOR[st] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{it.responsavelNome || '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Abrir">
                          <IconButton size="small" onClick={() => navigate(`/juridico/oficios/${it.id}`)}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/juridico/oficios/${it.id}/editar`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {podeExcluirLocal(it) && (
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setExcluir({ open: true, item: it, loading: false })}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={excluir.open} onClose={() => !excluir.loading && setExcluir({ open: false, item: null, loading: false })}>
        <DialogTitle>Excluir ofício?</DialogTitle>
        <DialogContent>
          <Typography>
            Confirma a exclusão de <strong>{excluir.item?.assunto}</strong>? (exclusão lógica)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={excluir.loading} onClick={() => setExcluir({ open: false, item: null, loading: false })}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" disabled={excluir.loading} onClick={confirmarExclusao}>
            {excluir.loading ? <CircularProgress size={18} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
